import { useContext, useCallback, forwardRef } from 'react';
import { motion } from 'motion/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBars } from '@fortawesome/pro-regular-svg-icons';

import Loader from '@components/Loader/Loader';
import Tooltip from '@components/Tooltip/Tooltip';

import useScrollBasedPin from '@hooks/use-scroll-based-pin';
import useNetworkStatus from '@hooks/use-network-status';
import { layoutContext } from '@context/layout';

import styles from './TopNav.module.scss';

interface TopNavProps {
  alwaysPinned?: boolean;
  showSearch?: boolean;
  showTasks?: boolean;
  showMobileOptionBars?: boolean;
  size?: 'small' | 'large';
}

const TopNav: React.FC<React.PropsWithChildren<TopNavProps>> = ({
  children,
  alwaysPinned = true,
  size = 'small',
  showMobileOptionBars = true,
}) => {
  const { dispatch: navDispatch } = useContext(layoutContext);

  const isPinned = useScrollBasedPin(true, { pinThreshold: 150 });
  const isVisible = alwaysPinned || isPinned;

  const networkStatus = useNetworkStatus();

  const handleOpenSideNav = useCallback(() => {
    navDispatch({ type: 'APPEAR', override: true });
  }, [navDispatch]);

  return (
    <header className={styles['navbar']} data-show={isVisible} data-size={size}>
      <div className={styles['navbar-content']}>
        {showMobileOptionBars && (
          <button className={styles['menu-icon-container']} onClick={handleOpenSideNav}>
            <FontAwesomeIcon icon={faBars} />
          </button>
        )}

        <div className={styles['nav-content']}>{children}</div>
      </div>

      {networkStatus === 'offline' && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className={styles['offline-warning']}>
          You are currently offline. Any changes that you make will not be persisted!
        </motion.div>
      )}
    </header>
  );
};

const PageOptions: React.FC<React.PropsWithChildren<unknown>> = ({ children }) => {
  return <div className={styles['page-options']}>{children}</div>;
};

interface PageOptionButtonProps {
  show?: boolean;
  onClick?: React.MouseEventHandler<HTMLButtonElement>;
  loading?: boolean;
  primary?: boolean;
  disabled?: boolean;
  explainer?: string;
}

const PageOptionButton = forwardRef<HTMLButtonElement, React.PropsWithChildren<PageOptionButtonProps>>(
  (
    { children, show = true, loading = false, primary = false, onClick, disabled = false, explainer },
    forwardedRef
  ) => {
    if (!show) return null;
    else
      return (
        <Tooltip size="small" label={explainer} aria-label="Setting explanation" show={!!explainer}>
          <motion.button
            disabled={disabled}
            className={styles['page-option-button']}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            onClick={onClick}
            ref={forwardedRef}
            data-primary={primary}
          >
            {!loading && children}
            {loading && <Loader />}
          </motion.button>
        </Tooltip>
      );
  }
);

PageOptionButton.displayName = 'PageOptionButton';

const PageOptionButtonIcon: React.FC<React.PropsWithChildren<unknown>> = ({ children }) => (
  <span className={styles['page-option-button-icon']}>{children}</span>
);

const PageOptionButtonText: React.FC<React.PropsWithChildren<unknown>> = ({ children }) => (
  <span className={styles['page-option-button-text']}>{children}</span>
);

const AlignedContent: React.FC<React.PropsWithChildren<{ align?: 'left' | 'right' | 'center' }>> = ({
  align = 'left',
  children,
}) => (
  <div className={styles['alligned-content']} data-align={align}>
    {children}
  </div>
);

export default TopNav;
export { PageOptionButton, PageOptionButtonIcon, PageOptionButtonText, AlignedContent, PageOptions };
